import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge, Loader2, RotateCcw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Common/Card';
import { Button } from '../Common/button';
import { ConfirmationModal } from '../Common/ConfirmationModal';
import notify from '../../lib/notify';
import aiPromptService, { describeApiError } from '../../services/aiPromptService';
import type { AnalysisLimits } from '../../types/projectAnalysis';
import { AnalysisLimitsTable, draftFrom, parseDraft, type LimitsDraft } from './AnalysisLimitsTable';

/** « Plafonds de l'analyse » (God): the global values, empty = code default. */
export const GlobalAnalysisLimitsCard: React.FC = () => {
  const { t } = useTranslation();
  const [limits, setLimits] = useState<AnalysisLimits | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState<LimitsDraft>({});
  const [errors, setErrors] = useState<Record<string, true>>({});
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const apply = useCallback((data: AnalysisLimits) => {
    setLimits(data);
    setDraft(draftFrom(data.global_values));
    setErrors({});
  }, []);

  useEffect(() => {
    aiPromptService
      .limits()
      .then(apply)
      .catch(() => setLoadError(true));
  }, [apply]);

  const save = async (reset: boolean) => {
    if (!limits) return;
    let payload: Record<string, number> | null = null;
    if (!reset) {
      const parsed = parseDraft(draft, limits);
      setErrors(parsed.errors);
      if (Object.keys(parsed.errors).length) {
        notify.error(t('projectAnalysisLimits.invalid'));
        return;
      }
      payload = Object.keys(parsed.values).length ? parsed.values : null;
    }
    setSaving(true);
    try {
      apply(await aiPromptService.saveGlobalLimits(payload));
      setConfirmReset(false);
      notify.success(reset ? t('projectAnalysisLimits.resetDone') : t('projectAnalysisLimits.saved'));
    } catch (error) {
      notify.error(describeApiError(error, t('projectAnalysisLimits.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gauge className="h-4 w-4" />
          {t('projectAnalysisLimits.globalTitle')}
        </CardTitle>
        <CardDescription>{t('projectAnalysisLimits.globalDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadError ? (
          <p className="text-sm text-destructive">{t('projectAnalysisLimits.loadError')}</p>
        ) : !limits ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : (
          <>
            <AnalysisLimitsTable
              limits={limits}
              inheritedOf={(key) => limits.defaults[key]}
              inheritedLabel={t('projectAnalysisLimits.columns.default')}
              editor={{ draft, onChange: setDraft, errors, disabled: saving }}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setConfirmReset(true)}
                disabled={saving || !Object.keys(limits.global_values).length}
              >
                <RotateCcw className="h-4 w-4" />
                {t('projectAnalysisLimits.resetDefaults')}
              </Button>
              <Button size="sm" onClick={() => void save(false)} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('projectAnalysisLimits.save')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
      <ConfirmationModal
        open={confirmReset}
        variant="warning"
        title={t('projectAnalysisLimits.resetTitle')}
        message={t('projectAnalysisLimits.resetMessage')}
        confirmText={t('projectAnalysisLimits.resetDefaults')}
        cancelText={t('global.cancel')}
        onConfirm={() => void save(true)}
        onCancel={() => setConfirmReset(false)}
        loading={saving}
      />
    </Card>
  );
};

export default GlobalAnalysisLimitsCard;
